import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Capas flotantes de la consola: velo, cajón lateral, modal, aviso efímero y el
// globo que sigue al puntero sobre los gráficos.
//
// En el prototipo eran cuatro nodos sueltos en <body> manipulados por
// `drawer()` / `modal()` / `toast()` / `tip()`. Acá son un contexto: cualquier
// pantalla pide `const ov = useOverlay()` y llama `ov.drawer({...})`. El
// contenido es JSX, no cadenas de HTML.
//
// Los cuatro viven DENTRO de la raíz `.yb-c` (no en un portal a body): ningún
// ancestro de la consola lleva `backdrop-filter`, así que `position:fixed` se
// resuelve contra la ventana como corresponde. Ver bitácora — el problema del
// containing block es de la web, donde el velo del tour sí cuelga de un
// ancestro con desenfoque.
// ─────────────────────────────────────────────────────────────────────────────

type Panel = {
  title: React.ReactNode;
  sub?: React.ReactNode;
  body: React.ReactNode;
  foot?: React.ReactNode;
  /** Ancho del cajón. Por defecto el del prototipo. */
  wide?: boolean;
};

type OverlayApi = {
  drawer: (p: Panel) => void;
  modal: (p: Panel) => void;
  cerrar: () => void;
  toast: (m: React.ReactNode) => void;
  /** Globo del gráfico: contenido + posición del puntero. */
  tip: (e: { clientX: number; clientY: number }, content: React.ReactNode) => void;
  tipOff: () => void;
};

const Ctx = createContext<OverlayApi | null>(null);

export function useOverlay(): OverlayApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useOverlay fuera de <OverlayProvider>');
  return api;
}

export function OverlayProvider({ children }: { children: React.ReactNode }) {
  const [drw, setDrw] = useState<Panel | null>(null);
  const [mdl, setMdl] = useState<Panel | null>(null);
  const [msg, setMsg] = useState<React.ReactNode>(null);
  const [tipState, setTipState] = useState<{
    x: number;
    y: number;
    node: React.ReactNode;
  } | null>(null);

  const tRef = useRef<any>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  const cerrar = useCallback(() => {
    setDrw(null);
    setMdl(null);
  }, []);

  const api = useMemo<OverlayApi>(
    () => ({
      drawer: (p) => {
        setMdl(null);
        setDrw(p);
      },
      modal: (p) => {
        setDrw(null);
        setMdl(p);
      },
      cerrar,
      toast: (m) => {
        setMsg(m);
        clearTimeout(tRef.current);
        tRef.current = setTimeout(() => setMsg(null), 3600);
      },
      tip: (e, node) => setTipState({ x: e.clientX, y: e.clientY, node }),
      tipOff: () => setTipState(null),
    }),
    [cerrar]
  );

  // Escape cierra la capa de arriba. Un cajón abierto bloquea el scroll de
  // fondo: si no, el puntero arrastra la página detrás del velo.
  useEffect(() => {
    const abierto = !!(drw || mdl);
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [drw, mdl, cerrar]);

  useEffect(() => () => clearTimeout(tRef.current), []);

  // El globo se ubica DESPUÉS de medirse: si se pega al borde derecho o
  // inferior, salta al otro lado del puntero en vez de salirse de la ventana.
  useEffect(() => {
    const el = tipRef.current;
    if (!el || !tipState) return;
    const r = el.getBoundingClientRect();
    let x = tipState.x + 16;
    let y = tipState.y - r.height - 14;
    if (x + r.width > window.innerWidth - 12) x = tipState.x - r.width - 16;
    if (y < 12) y = tipState.y + 20;
    el.style.transform = `translate(${Math.max(8, x)}px, ${Math.max(8, y)}px)`;
  }, [tipState]);

  return (
    <Ctx.Provider value={api}>
      {children}

      {/* ── fase67 · LAS CAPAS VIVEN BAJO LA RAÍZ DE LA CONSOLA ───────────────
          Toda la hoja está prefijada con `.yb-c` para que el reset del prototipo
          no reviente los componentes de MUI que siguen vivos. Pero este
          proveedor monta ARRIBA del armazón (`app-layout`: OverlayProvider →
          ConsoleShell), así que los cinco nodos quedaban de HERMANOS de `.yb-c`
          y ni una regla los alcanzaba: `.yb-c .drw` no matchea a un `.drw` que
          no cuelga de `.yb-c`. Medido: las cinco daban `position: static`. En la
          práctica, desde fase62 el cajón del APR, los modales de confirmación,
          los avisos y los globos de los gráficos se dibujaban como bloques
          sueltos al pie de la página.

          El arreglo es un envoltorio, no una mudanza: se les da la raíz `.yb-c`
          —que además les devuelve la tipografía y el reset de botones que el
          contenido del cajón necesita— y `.yb-ov` le saca al envoltorio lo que
          sólo tiene sentido en la página (fondo y relleno). `position: fixed`
          se sigue resolviendo contra la ventana porque ningún ancestro lleva
          `backdrop-filter` ni `transform`. */}
      <div className="yb-c yb-ov">
        <div
          className={`scrim${drw || mdl ? ' on' : ''}`}
          onClick={cerrar}
          aria-hidden={!(drw || mdl)}
        />

        <aside className={`drw${drw ? ' on' : ''}`} role="dialog" aria-modal={!!drw}>
          {drw && (
            <>
              <div className="drw-h">
                <div>
                  <div style={{ fontSize: '1.02rem' }}>{drw.title}</div>
                  {drw.sub ? (
                    <div className="sml" style={{ marginTop: 5 }}>
                      {drw.sub}
                    </div>
                  ) : null}
                </div>
                <button className="iconbtn" onClick={cerrar} title="Cerrar" aria-label="Cerrar">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>
              <div className="drw-b">{drw.body}</div>
              {drw.foot ? <div className="drw-f">{drw.foot}</div> : null}
            </>
          )}
        </aside>

        <div className={`mdl${mdl ? ' on' : ''}`} role="dialog" aria-modal={!!mdl}>
          {mdl && (
            <>
              <div className="mdl-h">
                <div style={{ fontSize: '1.02rem' }}>{mdl.title}</div>
                {mdl.sub ? (
                  <div className="sml" style={{ marginTop: 5 }}>
                    {mdl.sub}
                  </div>
                ) : null}
              </div>
              <div className="mdl-b">{mdl.body}</div>
              {mdl.foot ? <div className="mdl-f">{mdl.foot}</div> : null}
            </>
          )}
        </div>

        <div className={`ftip${tipState ? ' on' : ''}`} ref={tipRef} aria-hidden>
          {tipState?.node}
        </div>

        <div className={`toast${msg ? ' on' : ''}`} role="status">
          {msg}
        </div>
      </div>
    </Ctx.Provider>
  );
}
